import { createContext, useContext, useId, useMemo, type ComponentProps, type ReactNode } from 'react';
import * as Recharts from 'recharts';

import { cn } from '@/lib/utils';

/**
 * shadcn/ui's chart wrapper over Recharts, with one deliberate omission.
 *
 * Upstream ships a `ChartStyle` component that injects a `<style>` tag via
 * `dangerouslySetInnerHTML`, emitting `--color-<key>` custom properties once
 * per theme so a series can be written as `fill="var(--color-desktop)"`. That
 * exists to solve a problem this console does not have: it has no hardcoded
 * palette to switch between. Every colour here is already a semantic token
 * that resolves per theme on its own, so a config entry carries the `var()`
 * directly and the series uses it. One less injected stylesheet, and no
 * `dangerouslySetInnerHTML` to justify in review.
 *
 * Recharts sets `item.color` on the tooltip payload from whatever stroke or
 * fill the series was given, so the tooltip swatches stay correct without the
 * indirection.
 */
export type ChartConfig = Record<
  string,
  {
    readonly label?: ReactNode;
    readonly icon?: React.ComponentType;
    /** A CSS colour. Use a semantic token: `var(--color-primary)`. */
    readonly color?: string;
  }
>;

interface ChartContextValue {
  readonly config: ChartConfig;
}

const ChartContext = createContext<ChartContextValue | null>(null);

function useChart(): ChartContextValue {
  const context = useContext(ChartContext);

  if (context === null) {
    throw new Error('Chart components must be used inside <ChartContainer>.');
  }

  return context;
}

export function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: ComponentProps<'div'> & {
  readonly config: ChartConfig;
  readonly children: ComponentProps<typeof Recharts.ResponsiveContainer>['children'];
}) {
  const uniqueId = useId();
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`;
  const value = useMemo<ChartContextValue>(() => ({ config }), [config]);

  return (
    <ChartContext.Provider value={value}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          'flex justify-center text-xs',
          // Recharts hardcodes #ccc and #fff on several internals; these
          // repoint them at the theme rather than forking the library.
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50",
          "[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent",
          '[&_.recharts-layer]:outline-hidden [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted',
          "[&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          className,
        )}
        {...props}
      >
        <Recharts.ResponsiveContainer initialDimension={{ width: 320, height: 200 }}>
          {children}
        </Recharts.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

export const ChartTooltip = Recharts.Tooltip;

type TooltipValue = Recharts.TooltipValueType;
type TooltipName = number | string;

export function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: ComponentProps<typeof Recharts.Tooltip> &
  ComponentProps<'div'> & {
    readonly hideLabel?: boolean;
    readonly hideIndicator?: boolean;
    readonly indicator?: 'line' | 'dot' | 'dashed';
    readonly nameKey?: string;
    readonly labelKey?: string;
  } & // `payload` and `label` are injected into the `content` element at render
  // time rather than declared on `<Tooltip>` itself, so they come from the
  // default content's props instead. `accessibilityLayer` is Recharts'
  // internal flag and is not ours to accept.
  Omit<Recharts.DefaultTooltipContentProps<TooltipValue, TooltipName>, 'accessibilityLayer'>) {
  const { config } = useChart();

  if (active !== true || payload === undefined || payload.length === 0) return null;

  const [first] = payload;
  const labelItemKey = String(labelKey ?? first?.dataKey ?? first?.name ?? 'value');
  const labelConfig = configFor(config, first, labelItemKey);
  const labelValue =
    labelKey === undefined && typeof label === 'string'
      ? (config[label]?.label ?? label)
      : labelConfig?.label;

  const renderedLabel = hideLabel ? null : labelFormatter !== undefined ? (
    <div className={cn('font-medium', labelClassName)}>{labelFormatter(labelValue, payload)}</div>
  ) : labelValue === undefined ? null : (
    <div className={cn('font-medium', labelClassName)}>{labelValue}</div>
  );

  const nestLabel = payload.length === 1 && indicator !== 'dot';

  return (
    <div
      className={cn(
        'grid min-w-[8rem] items-start gap-1.5 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-lg',
        className,
      )}
    >
      {nestLabel ? null : renderedLabel}
      <div className="grid gap-1.5">
        {payload
          .filter((item) => item.type !== 'none')
          .map((item, index) => {
            const key = String(nameKey ?? item.name ?? item.dataKey ?? 'value');
            const itemConfig = configFor(config, item, key);
            const swatch = color ?? item.color;

            return (
              <div
                key={key + String(index)}
                className={cn(
                  'flex w-full flex-wrap items-stretch gap-2 [&>svg]:size-2.5 [&>svg]:text-muted-foreground',
                  indicator === 'dot' && 'items-center',
                )}
              >
                {formatter !== undefined && item.value !== undefined && item.name !== undefined ? (
                  formatter(item.value, item.name, item, index, payload)
                ) : (
                  <>
                    {itemConfig?.icon !== undefined ? (
                      <itemConfig.icon />
                    ) : hideIndicator ? null : (
                      <div
                        className={cn('shrink-0 rounded-[2px]', {
                          'size-2.5': indicator === 'dot',
                          'w-1': indicator === 'line',
                          'w-0 border-[1.5px] border-dashed bg-transparent': indicator === 'dashed',
                          'my-0.5': nestLabel && indicator === 'dashed',
                        })}
                        style={{ backgroundColor: swatch, borderColor: swatch }}
                      />
                    )}
                    <div
                      className={cn(
                        'flex flex-1 justify-between leading-none',
                        nestLabel ? 'items-end' : 'items-center',
                      )}
                    >
                      <div className="grid gap-1.5">
                        {nestLabel ? renderedLabel : null}
                        <span className="text-muted-foreground">{itemConfig?.label ?? item.name}</span>
                      </div>
                      {item.value === undefined ? null : (
                        <span className="font-mono font-medium text-foreground tabular-nums">
                          {typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export const ChartLegend = Recharts.Legend;

export function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  nameKey,
}: ComponentProps<'div'> & {
  readonly hideIcon?: boolean;
  readonly nameKey?: string;
} & Recharts.DefaultLegendContentProps) {
  const { config } = useChart();

  if (payload === undefined || payload.length === 0) return null;

  // Upstream switches the padding side on `verticalAlign`, which Recharts has
  // deprecated. Every legend in this console sits under its chart, so the
  // branch bought nothing but a deprecated read.
  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-4 pt-3', className)}>
      {payload
        .filter((item) => item.type !== 'none')
        .map((item, index) => {
          const key = String(nameKey ?? item.dataKey ?? 'value');
          const itemConfig = configFor(config, item, key);

          return (
            <div
              key={key + String(index)}
              className="flex items-center gap-1.5 text-muted-foreground [&>svg]:size-3"
            >
              {itemConfig?.icon !== undefined && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: item.color }} />
              )}
              {itemConfig?.label ?? key}
            </div>
          );
        })}
    </div>
  );
}

/**
 * Recharts hands a payload whose shape depends on the chart type, so the key a
 * config entry lives under can be on the item or one level down in its own
 * `payload`. Checks both before falling back to the raw key.
 */
function configFor(config: ChartConfig, item: unknown, key: string): ChartConfig[string] | undefined {
  if (typeof item !== 'object' || item === null) return undefined;

  const record = item as Record<string, unknown>;
  const inner = typeof record.payload === 'object' && record.payload !== null ? record.payload : undefined;

  const fromItem = record[key];
  if (typeof fromItem === 'string' && fromItem in config) return config[fromItem];

  if (inner !== undefined) {
    const fromInner = (inner as Record<string, unknown>)[key];
    if (typeof fromInner === 'string' && fromInner in config) return config[fromInner];
  }

  return config[key];
}

/**
 * The same numbers the chart draws, for anyone not looking at it.
 *
 * Wrapped in a `<div>` rather than putting `sr-only` on the `<table>` itself,
 * and that detail is load-bearing. `sr-only` hides by shrinking the box to 1px
 * and clipping it, but a `<table>` sizes to its content and ignores a width
 * that small. The table therefore kept its full size, and because `sr-only` is
 * `position: absolute` with no positioned ancestor it resolved against the
 * viewport instead of the scroll container, adding its whole height to the
 * document. The visible symptom was the app sidebar scrolling along with the
 * page on the one screen that had a chart. A `<div>` is a block box, so it
 * honours the 1px and clips the table inside it.
 */
export function ChartDataTable({
  caption,
  columns,
  rows,
}: {
  readonly caption: string;
  readonly columns: readonly string[];
  readonly rows: readonly { readonly key: string; readonly cells: readonly string[] }[];
}) {
  return (
    // `relative` so the `sr-only` box below resolves against this wrapper
    // rather than the viewport, wherever the chart is rendered. `AppShell`
    // positions `<main>` for the same reason, but a chart should not depend on
    // its host to avoid dragging the page's scrollbar around.
    <div className="relative">
      <div className="sr-only">
        <table>
          <caption>{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, index) =>
                  index === 0 ? (
                    <th key={index} scope="row">
                      {cell}
                    </th>
                  ) : (
                    <td key={index}>{cell}</td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
